import {
  IrpfAscendantDto,
  IrpfDescendantDto,
  IrpfDto,
  IrpfIndividualDto,
  IrpfPatchedDto,
  IrpfPensionPaymentDto
} from '@app/data/dtos/ptrab/irpf/irpf.dto';
import { MaritalStatusEnum, Territories } from '@app/entities/enums/ptrab/irpf.enum';
import { Ancestor, Descendant, IrpfModel, PensionPayment, Worker } from '@app/entities/models/ptrab/irpf/irpf.model';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

export const mapIrpf: MapFromFn<IrpfDto, IrpfModel> = (value) =>
  Builder.forModel(IrpfModel)
    .individual(mapWorker(value.pit_information.individual))
    .descendants(mapIrpfDescendants(value.pit_information.descendants))
    .ascendants(mapIrpfAscendants(value.pit_information.ascendants))
    .pensionPayment(mapPensionPayment(value.pit_information.support_payment))
    .mortgageReduction(value.pit_information.mortgage_reduction.mortgage_reduction)
    .ceutaMelillaResidence(value.pit_information.ceuta_melilla_residence.ceuta_melilla_residence)
    .territory(value.pit_information.territory as Territories)
    .build();

export const mapPatchedIrpf: MapFromFn<IrpfPatchedDto, IrpfModel> = (value) =>
  Builder.forModel(IrpfModel)
    .individual(mapWorker(value.data.pit_information.individual))
    .descendants(mapIrpfDescendants(value.data.pit_information.descendants))
    .ascendants(mapIrpfAscendants(value.data.pit_information.ascendants))
    .pensionPayment(mapPensionPayment(value.data.pit_information.support_payment))
    .mortgageReduction(value.data.pit_information.mortgage_reduction.mortgage_reduction)
    .ceutaMelillaResidence(value.data.pit_information.ceuta_melilla_residence.ceuta_melilla_residence)
    .territory(value.data.pit_information.territory as Territories)
    .build();

export const mapWorker: MapFromFn<IrpfIndividualDto, Worker> = (value) =>
  Builder.forModel(Worker)
    .disability(value.disability)
    .maritalStatus(value.marital_status as MaritalStatusEnum)
    .spouseNif(value.spouse_nif)
    .reducedMobility(value.reduced_mobility)
    .geographicMobilityDate(value.geographic_mobility_date)
    .build();

export const mapWorkerDto: MapFromFn<Worker, IrpfIndividualDto> = (model) =>
  Builder.forDto<IrpfIndividualDto>()
    .disability(model.disability)
    .marital_status(model.maritalStatus)
    .spouse_nif(model.spouseNif)
    .reduced_mobility(model.reducedMobility)
    .geographic_mobility_date(model.geographicMobilityDate)
    .build();

export const mapPensionPayment: MapFromFn<IrpfPensionPaymentDto, PensionPayment> = (value) =>
  Builder.forModel(PensionPayment)
    .alimony(value.alimony)
    .childAllowance(value.child_allowance)
    .courtRulingDate(value.court_ruling_date ?? '')
    .build();

export const mapPensionPaymentDto: MapFromFn<PensionPayment, IrpfPensionPaymentDto> = (model) =>
  Builder.forDto<IrpfPensionPaymentDto>()
    .alimony(model.alimony)
    .child_allowance(model.childAllowance)
    .court_ruling_date(model.courtRulingDate)
    .build();

export const mapIrpfAscendants: MapFromFn<IrpfAscendantDto[], Ancestor[]> = (value) => value.map(mapIrpfAscendant);

export const mapIrpfAscendant: MapFromFn<IrpfAscendantDto, Ancestor> = (value) =>
  Builder.forModel(Ancestor)
    .birthDate(value.birth_date)
    .disability(value.disability)
    .reducedMobility(value.reduced_mobility)
    .numberDescendantLiveTogether(value.number_descendant_live_together)
    .build();

export const mapAscendantsDto: MapFromFn<Ancestor[], IrpfAscendantDto[]> = (value) => value.map(mapAscendantDto);

export const mapAscendantDto: MapFromFn<Ancestor, IrpfAscendantDto> = (value) =>
  Builder.forDto<IrpfAscendantDto>()
    .birth_date(value.birthDate)
    .disability(value.disability)
    .reduced_mobility(value.reducedMobility)
    .number_descendant_live_together(value.numberDescendantLiveTogether)
    .build();

export const mapIrpfDescendants: MapFromFn<IrpfDescendantDto[], Descendant[]> = (value) => value.map(mapIrpfDescendant);

export const mapIrpfDescendant: MapFromFn<IrpfDescendantDto, Descendant> = (value) =>
  Builder.forModel(Descendant)
    .birthDate(value.birth_date)
    .disability(value.disability)
    .reducedMobility(value.reduced_mobility)
    .adoptionDate(value.adoption_date ?? 0)
    .soleCustody(value.sole_custody)
    .build();

export const mapDescendantsDto: MapFromFn<Descendant[], IrpfDescendantDto[]> = (value) => value.map(mapDescendantDto);

export const mapDescendantDto: MapFromFn<Descendant, IrpfDescendantDto> = (value) =>
  Builder.forDto<IrpfDescendantDto>()
    .birth_date(value.birthDate)
    .disability(value.disability)
    .reduced_mobility(value.reducedMobility)
    .adoption_date(value.adoptionDate)
    .sole_custody(value.soleCustody)
    .build();
